/**
 * Authentication Context Provider
 * 
 * This context provides global authentication state management for the entire
 * application. It handles user login/logout, registration, and maintains the
 * current authentication status throughout the app lifecycle.
 * 
 * Key features:
 * - Centralized authentication state management
 * - Automatic token persistence and restoration
 * - Loading states for auth operations
 * - Type-safe context with proper error handling
 * - Integration with backend authentication service
 * 
 * Usage:
 * - Wrap your app with <AuthProvider>
 * - Use useAuth() hook in any component to access auth state/methods
 * - Protected routes can check isAuthenticated status
 * - User information is available globally once authenticated
 */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import type { User, CreateUserRequest, LoginRequest } from '@trading-app/shared';
import { authService } from '../services/auth';
import { tradingService } from '../services/trading';

// Type definition for authentication context values
interface AuthContextType {
  user: User | null;                                    // Current user data or null if not authenticated
  isAuthenticated: boolean;                            // True if user is currently authenticated
  isLoading: boolean;                                 // True while checking/updating auth status
  login: (data: LoginRequest) => Promise<{ success: boolean; message?: string }>;
  register: (data: CreateUserRequest) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;                                 // Method to clear authentication state
  /**
   * Annie Mode Phase 8 (PR #152 + #153): optimistic flip of user.activeMode.
   * Web equivalent of mobile's AuthContext.updateActiveMode. Updates the
   * local React state synchronously so ModeThemeBinder sees the new value,
   * fires PATCH, rolls back on failure.
   */
  updateActiveMode: (mode: 'ERIC' | 'ANNIE') => Promise<void>;
}

// Create React context with undefined default (requires useAuth hook for access)
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Props interface for AuthProvider component
interface AuthProviderProps {
  children: ReactNode; // Child components that will have access to auth context
}

/**
 * Authentication Provider Component
 * 
 * Manages authentication state and provides auth methods to child components.
 * Automatically initializes auth state on app startup by checking for stored tokens.
 * 
 * @param children - Child components that need access to authentication context
 */
export function AuthProvider({ children }: AuthProviderProps) {
  // Authentication state management
  const [user, setUser] = useState<User | null>(null);  // Current user data
  const [isLoading, setIsLoading] = useState(true);     // Loading state for auth operations

  // Computed authentication status based on user presence
  const isAuthenticated = !!user;

  // Initialize authentication state when the app starts
  // Checks for stored tokens and validates them with the backend
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);
      try {
        console.log('Initializing authentication...');
        // Try to restore authentication from stored tokens
        const success = await authService.initialize();
        console.log('Auth initialization result:', success);
        if (success) {
          // Get user data from the auth service if initialization succeeds
          const currentUser = authService.getUser();
          console.log('Current user:', currentUser);
          setUser(currentUser);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Auth initialization failure is handled gracefully (user starts logged out)
      } finally {
        console.log('Auth initialization complete, setting loading to false');
        setIsLoading(false); // Always stop loading, regardless of success/failure
      }
    };

    initializeAuth();
  }, []); // Run once on component mount

  /**
   * User login method
   * 
   * Attempts to authenticate user with email and password. Updates the global
   * user state on successful login.
   * 
   * @param data - Login credentials (email and password)
   * @returns Promise with success status and optional error message
   */
  const login = async (data: LoginRequest): Promise<{ success: boolean; message?: string }> => {
    try {
      // Call backend authentication service
      const result = await authService.login(data);
      
      if (result.success && result.user) {
        // Update global user state on successful login
        setUser(result.user);
        return { success: true };
      } else {
        // Return error message from backend
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      // Return generic error message for unexpected failures
      return { success: false, message: 'An error occurred during login' };
    }
  };

  /**
   * User registration method
   * 
   * Creates a new user account and automatically logs them in. Updates the
   * global user state on successful registration.
   * 
   * @param data - Registration data (email, password, optional firstName)
   * @returns Promise with success status and optional error message
   */
  const register = async (data: CreateUserRequest): Promise<{ success: boolean; message?: string }> => {
    try {
      // Call backend registration service
      const result = await authService.register(data);
      
      if (result.success && result.user) {
        // Update global user state on successful registration
        setUser(result.user);
        return { success: true };
      } else {
        // Return error message from backend
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Registration error:', error);
      // Return generic error message for unexpected failures
      return { success: false, message: 'An error occurred during registration' };
    }
  };

  /**
   * User logout method
   * 
   * Clears authentication tokens and resets user state. Automatically
   * redirects user to login page via route protection.
   */
  const logout = () => {
    authService.logout(); // Clear stored tokens and auth state
    setUser(null);        // Clear user from global state
  };

  // Annie Mode Phase 8 (Codex #152 F1 fix on PR #153):
  // Optimistic mode flip — update AuthContext.user.activeMode locally so the
  // ModeThemeBinder doesn't revert the theme change on its next re-render
  // (binder uses useAuth().user.activeMode as the source of truth). Roll
  // back to snapshot on PATCH failure.
  const userRef = useRef(user);
  userRef.current = user;
  const updateActiveMode = useCallback(
    async (mode: 'ERIC' | 'ANNIE'): Promise<void> => {
      const snapshot = userRef.current;
      if (!snapshot) throw new Error('No authenticated user');
      if (snapshot.activeMode === mode) return; // No-op — already in this mode.
      setUser({ ...snapshot, activeMode: mode });
      try {
        await tradingService.updateActiveMode(mode);
      } catch (err) {
        setUser(snapshot); // Rollback on PATCH failure.
        throw err;
      }
    },
    [],
  );

  // Create context value object with all auth state and methods
  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    updateActiveMode,
  };

  // Provide auth context to all child components
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Custom hook for accessing authentication context
 * 
 * Provides type-safe access to authentication state and methods. Must be used
 * within an AuthProvider component or will throw an error.
 * 
 * @returns Authentication context with user state and auth methods
 * @throws Error if used outside of AuthProvider
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}